import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';

@Component({
  selector: 'app-discussion-sidebar',
  standalone: true,
  imports: [CommonModule, LoginModalComponent],
  templateUrl: './discussion-sidebar.component.html',
  styleUrl: './discussion-sidebar.component.scss',
})
export class DiscussionSidebarComponent implements OnInit {
  @Input() activeTab: string = 'getting-started';

  /** Personalise tabs that require login; show login modal if not logged in. */
  private readonly PERSONALISE_TABS = ['my-discussions', 'my-bookmarks', 'my-pinned', 'post-submission'];

  showLoginModal = false;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private keycloak = inject(KeycloakService);

  categories = [
    { label: 'Getting Started', tab: 'getting-started', key: 'getting-started' },
    { label: 'Public', tab: 'public', key: 'public' },
    { label: 'General', tab: 'general', key: 'general' },
    { label: 'Feedback', tab: 'feedback', key: 'feedback' },
    { label: 'Announcement', tab: 'announcement', key: 'announcement' }
  ];

  personalise = [
    { label: 'My Discussions', tab: 'my-discussions', key: 'my-discussions' },
    { label: 'My Bookmarks', tab: 'my-bookmarks', key: 'my-bookmarks' },
    { label: 'My Pinned', tab: 'my-pinned', key: 'my-pinned' },
    { label: 'Post Submission', tab: 'post-submission', key: 'post-submission' }
  ];

  ngOnInit(): void {
    // Auto-detect active tab from route if not provided
    if (!this.activeTab || this.activeTab === '') {
      this.route.url.subscribe(urlSegments => {
        const routePath = urlSegments.length > 0 ? urlSegments[urlSegments.length - 1].path : '';
        // On create-new page: do not highlight any sidebar item (user may have come from Personalise)
        if (routePath === 'create-new') {
          this.activeTab = '';
          return;
        }
        if (urlSegments.length > 1) {
          const firstSegment = urlSegments[1].path;
          const routeToTabMap: { [key: string]: string } = {
            'getting-started': 'getting-started',
            'public': 'public',
            'general': 'general',
            'feedback': 'feedback',
            'announcement': 'announcement',
            'my-discussions': 'my-discussions',
            'my-bookmarks': 'my-bookmarks',
            'my-pinned': 'my-pinned',
            'post-submission': 'post-submission'
          };
          this.activeTab = routeToTabMap[firstSegment] || 'getting-started';
        } else {
          // Default to getting-started if on base /discussions route
          this.activeTab = 'getting-started';
        }
      });
    }
  }

  async navigateToTab(tab: string): Promise<void> {
    // Personalise items require login: check and show modal if not logged in
    if (this.PERSONALISE_TABS.includes(tab)) {
      try {
        const isLoggedIn = await this.keycloak.isLoggedIn();
        if (!isLoggedIn) {
          this.showLoginModal = true;
          return;
        }
      } catch {
        this.showLoginModal = true;
        return;
      }
    }

    // Map tab to route path
    const routeMap: { [key: string]: string } = {
      'getting-started': '/discussions/getting-started',
      'public': '/discussions/public',
      'general': '/discussions/general',
      'feedback': '/discussions/feedback',
      'announcement': '/discussions/announcement',
      'my-discussions': '/discussions/my-discussions',
      'my-bookmarks': '/discussions/my-bookmarks',
      'my-pinned': '/discussions/my-pinned',
      'post-submission': '/discussions/post-submission'
    };

    const route = routeMap[tab] || '/discussions/getting-started';
    this.router.navigate([route]);
  }

  isActive(item: { key: string }): boolean {
    return this.activeTab === item.key;
  }
}
